import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRouterBasename } from './useRouterBasename';

interface UseNavigationBlockerOptions {
  /**
   * Whether navigation should be blocked
   * @default true
   */
  shouldBlock?: boolean;
  /**
   * Callback when user confirms they want to leave
   */
  onConfirmLeave?: () => void;
  /**
   * Callback when user cancels leaving
   */
  onCancelLeave?: () => void;
}

interface UseNavigationBlockerReturn {
  /**
   * Whether the leave confirmation modal should be shown
   */
  showModal: boolean;
  /**
   * The destination the user was trying to navigate to
   */
  pendingNavigation: string | number | null;
  /**
   * Handler to call when user confirms they want to leave
   */
  confirmLeave: () => void;
  /**
   * Handler to call when user cancels leaving
   */
  cancelLeave: () => void;
  /**
   * Programmatic navigation handler that checks if blocking is needed
   */
  navigateAway: (destination: string | number) => void;
}

/**
 * Custom hook to block navigation and show a confirmation modal
 * when the user attempts to leave the current page.
 *
 * This hook intercepts:
 * - Browser back/forward buttons
 * - Navigation links (via click event delegation)
 * - Browser close/refresh (via beforeunload)
 *
 * @example
 * ```tsx
 * const { showModal, confirmLeave, cancelLeave } = useNavigationBlocker({
 *   shouldBlock: hasUnsavedChanges,
 * });
 *
 * return (
 *   <>
 *     <YourComponent />
 *     <Modal isOpen={showModal}>
 *       <button onClick={cancelLeave}>Stay</button>
 *       <button onClick={confirmLeave}>Leave</button>
 *     </Modal>
 *   </>
 * );
 * ```
 */
export const useNavigationBlocker = ({
  shouldBlock = true,
  onConfirmLeave,
  onCancelLeave,
}: UseNavigationBlockerOptions = {}): UseNavigationBlockerReturn => {
  const navigate = useNavigate();
  const { basename, stripBasename } = useRouterBasename();
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    string | number | null
  >(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const isInitializedRef = useRef(false);

  // Intercept all link clicks to check for unsaved changes
  useEffect(() => {
    if (!shouldBlock || isNavigating) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Find the closest anchor element
      const anchor = target.closest('a');

      if (!anchor) return;

      // Get the href
      const href = anchor.getAttribute('href');

      // Only intercept internal navigation links (not external links or non-router links)
      if (href && href.startsWith('/') && !href.startsWith('//')) {
        const currentPath = window.location.pathname;

        // Compare using router-relative paths to handle basename correctly
        const routerHref = stripBasename(href);
        const routerCurrentPath = stripBasename(currentPath);

        // Only intercept if navigating to a different page
        if (routerHref !== routerCurrentPath) {
          e.preventDefault();
          e.stopPropagation();
          // Store the router-relative path to avoid basename duplication
          setPendingNavigation(routerHref);
          setShowModal(true);
        }
      }
    };

    // Use capture phase to intercept before React Router processes the click
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [shouldBlock, stripBasename, isNavigating]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    // Bypass if should not block or already navigating
    if (!shouldBlock || isNavigating) return;

    // Reset initialization state when effect runs
    isInitializedRef.current = false;

    const handlePopState = () => {
      // Don't show modal if this is the initial setup
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        return;
      }

      // If we detect a back navigation attempt, show the modal
      setPendingNavigation(-1);
      setShowModal(true);

      // Immediately push the current state back to stay on the page
      const currentUrl = window.location.href;
      window.history.pushState({ blockerActive: true }, '', currentUrl);
    };

    // Set up the popstate listener first
    window.addEventListener('popstate', handlePopState);
    // This prevents the immediate trigger but sets up the blocking
    // The 50ms delay ensures the popstate listener is registered before adding the dummy history entry.
    // This avoids a race condition where the popstate event could fire immediately if the popstate event could fire immediately if the entry is added too soon.
    // The value 50ms was chosen as a brief, reliable delay that works across browsers without noticeable UI lag.
    const timeoutId = setTimeout(() => {
      const currentUrl = window.location.href;
      window.history.pushState({ blockerActive: true }, '', currentUrl);
      isInitializedRef.current = true;
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('popstate', handlePopState);
      // Only clean up if we actually added a history entry
      if (isInitializedRef.current && window.history.state?.blockerActive) {
        window.history.back();
      }
    };
  }, [shouldBlock, isNavigating]);

  // Browser-level protection for close/refresh
  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);

  // Internal navigation function that handles the actual navigation logic
  const performNavigation = useCallback(
    (destination: string | number) => {
      if (typeof destination === 'number') {
        navigate(destination); // delta for back/forward
      } else {
        // If destination is already a router-relative path (no basename), use it directly
        // Otherwise strip the basename to get the router-relative path
        const routerPath =
          basename && destination.startsWith(basename)
            ? stripBasename(destination)
            : destination;
        navigate(routerPath); // path for regular navigation
      }
    },
    [navigate, stripBasename, basename],
  );

  // Handler to confirm leaving
  const confirmLeave = useCallback(() => {
    if (pendingNavigation !== null) {
      setShowModal(false);
      setIsNavigating(true); // Disable blocking during navigation
      onConfirmLeave?.();

      // Navigate after state update
      setTimeout(() => {
        // If it's a back navigation, we need to handle it differently
        if (pendingNavigation === -1) {
          // Clean up our blocker state first, then go back twice
          // (once to remove our dummy entry, once to actually go back)
          if (window.history.state?.blockerActive) {
            window.history.back(); // Remove our dummy entry
            setTimeout(() => {
              window.history.back(); // Actually go back
            }, 10);
          } else {
            window.history.back(); // Just go back normally
          }
        } else {
          performNavigation(pendingNavigation);
        }
        setPendingNavigation(null);
        // Re-enable blocking after navigation completes
        setTimeout(() => setIsNavigating(false), 100);
      }, 0);
    }
  }, [pendingNavigation, performNavigation, onConfirmLeave]);

  // Handler to cancel leaving
  const cancelLeave = useCallback(() => {
    setShowModal(false);
    setPendingNavigation(null);
    onCancelLeave?.();
  }, [onCancelLeave]);

  // Programmatic navigation handler
  const navigateAway = useCallback(
    (destination: string | number) => {
      if (shouldBlock) {
        // Store the original destination - performNavigation will handle stripping
        setPendingNavigation(destination);
        setShowModal(true);
      } else {
        performNavigation(destination);
      }
    },
    [shouldBlock, performNavigation],
  );

  return {
    showModal,
    pendingNavigation,
    confirmLeave,
    cancelLeave,
    navigateAway,
  };
};
